import * as React from 'react';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials } from '@/selectors/selectors';
import { getCommsUsersForVerification, requestCommsUserVerification, CommsUserForVerification } from '@/api';
import { IntegrationType } from '@/types';
import { globalMessage } from '@/utils/globalMessage';

interface LinkTeamsAccountModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    orgId: string;
    providerId: string;
    providerName: string;
}

export const LinkTeamsAccountModal: React.FC<LinkTeamsAccountModalProps> = (props) => {
    const [form] = Form.useForm();
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [commsUsers, setCommsUsers] = React.useState<CommsUserForVerification[]>([]);
    const [loadingUsers, setLoadingUsers] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [verificationLinkToShow, setVerificationLinkToShow] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (props.open && props.providerId) {
            setLoadingUsers(true);
            getCommsUsersForVerification(props.orgId, props.providerId, IntegrationType.MicrosoftTeams, auth)
                .then(setCommsUsers)
                .catch((err) => {
                    globalMessage.error(err?.message ?? 'Failed to load Teams users');
                    setCommsUsers([]);
                })
                .finally(() => setLoadingUsers(false));
        }
    }, [props.open, props.providerId, props.orgId, auth]);

    const handleSubmit = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            const response = await requestCommsUserVerification(
                {
                    orgId: props.orgId,
                    commsUserId: values.commsUserId,
                    providerId: props.providerId,
                    integrationType: IntegrationType.MicrosoftTeams,
                },
                auth,
            );
            const verificationLink = response?.verificationLink;
            if (verificationLink) {
                globalMessage.success("Couldn't send DM in Teams. Use the link below to complete verification.");
                setVerificationLinkToShow(verificationLink);
            } else {
                globalMessage.success(
                    'Verification link sent! Check your Teams chat for a message from Runway. Click the link to complete sign-in.',
                );
                props.onSuccess();
                props.onCancel();
            }
        } catch (err) {
            globalMessage.error(err?.message ?? 'Failed to send verification link');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setVerificationLinkToShow(null);
        props.onSuccess();
        props.onCancel();
    };

    return (
        <Modal
            title={`Link ${props.providerName} account`}
            open={props.open}
            onCancel={() => {
                setVerificationLinkToShow(null);
                props.onCancel();
            }}
            onOk={verificationLinkToShow ? undefined : handleSubmit}
            confirmLoading={submitting}
            okText="Send verification link"
            destroyOnClose
            footer={
                verificationLinkToShow ?
                    <Button type="primary" onClick={handleClose}>
                        Done
                    </Button>
                :   undefined
            }
        >
            {verificationLinkToShow ?
                <div>
                    <div style={{ marginBottom: 12 }}>
                        Copy this link and open it in your browser to complete verification:
                    </div>
                    <Input.Group compact style={{ display: 'flex' }}>
                        <Input
                            readOnly
                            value={verificationLinkToShow}
                            style={{ flex: 1 }}
                            data-testid="verification-link-input"
                        />
                        <Button
                            type="primary"
                            onClick={() => {
                                navigator.clipboard.writeText(verificationLinkToShow);
                                globalMessage.success('Link copied to clipboard');
                            }}
                        >
                            Copy
                        </Button>
                        <Button
                            onClick={() => {
                                window.open(verificationLinkToShow, '_blank');
                            }}
                        >
                            Open
                        </Button>
                    </Input.Group>
                </div>
            :   <>
                    <div style={{ marginBottom: 16 }}>
                        Select your Teams account below. We'll send you a direct message in Teams with a link to verify
                        and link your Runway account.
                    </div>
                    <Form form={form} layout="vertical" requiredMark={false}>
                        <Form.Item
                            name="commsUserId"
                            label="Your Teams account"
                            rules={[{ required: true, message: 'Please select your Teams account' }]}
                        >
                            <Select
                                placeholder={loadingUsers ? 'Loading...' : 'Select your Teams account'}
                                loading={loadingUsers}
                                showSearch
                                optionFilterProp="label"
                                options={commsUsers.map((u) => ({
                                    value: u.id,
                                    label:
                                        u.displayName ?
                                            `${u.displayName}${u.email ? ` (${u.email})` : ''}`
                                        :   u.email || u.id,
                                }))}
                            />
                        </Form.Item>
                    </Form>
                </>
            }
        </Modal>
    );
};
